/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { BarcodeOutlined } from '@ant-design/icons';
import {
  Modal,
  Col,
  Row,
  Spin,
} from 'antd';
import {
  Title, Button, Text,
} from 'components';

import { printReferenceOrder } from 'api/recaudacion';
import Previewer from 'components/Previewer';
import _logger from 'utils/logger';

// Estilos constantes
const styles = {
  modal: {
    width: 700,
    bodyStyle: { padding: 0 },
    cancelButtonProps: {
      style: { color: '#fff' },
    },
  },
  header: {
    background: '#681B2D',
    padding: '24px 0',
    textAlign: 'center',
  },
  container: {
    overflow: 'hidden',
    background: '#fff',
  },
  leftColumn: {
    padding: '32px 24px 24px 24px',
    background: '#fff',
  },
  rightColumn: {
    background: '#F7F7F7',
    padding: '32px 18px',
    borderLeft: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  infoBox: {
    background: '#F7F7F7',
    padding: 16,
    borderRadius: 12,
    marginTop: 16,
    gap: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  infoText: {
    fontSize: 15,
  },
  instructionsList: {
    paddingLeft: 18,
    fontSize: 16,
    color: '#333',
    marginBottom: 18,
  },
  instructionItem: {
    marginBottom: 10,
  },
  rightColumnTitle: {
    color: '#681B2D',
    fontWeight: 'bold',
    fontSize: 17,
    marginBottom: 12,
    textAlign: 'center',
  },
  beneficiaryInfo: {
    fontSize: 15,
    marginBottom: 8,
  },
  footer: {
    padding: 24,
    background: '#fff',
    textAlign: 'center',
    borderTop: '1px solid #eee',
  },
  generateButton: {
    minWidth: 260,
    fontSize: 16,
    height: 44,
  },
  buttonIcon: {
    marginRight: 8,
    fontSize: 18,
  },
};

// Datos constantes
const TRANSFER_DATA = {
  beneficiary: 'MUNICIPIO DE NAVOJOA',
  bank: 'BANORTE',
  clabe: '072 777 010885248228',
  rfc: 'MNA150930AQ4',
  phone: '642 4256300',
  address: 'NO REELECCIÓN Y PLAZA 5 DE MAYO S/N COL. CENTRO C.P. 85800, NAVOJOA, SONORA',
};

const INSTRUCTIONS = [
  'Genere su transferencia de pago correspondiente, la cual puedes generar desde el botón de ubicado en la parte inferior con el texto "Generar orden de transferencia interbancaria"',
  'Una vez generado el formato correspondiente, ingresa a tu banca en línea.',
  'Por primera vez, realice el alta del beneficiario, de lo contrario, agregue la cuenta clabe interbancaria correspondiente y proceda al alta.',
  'El formato generado contiene una referencia de pago de a 20 digitos la cual se debera colocar en el campo concepto de su banca electrónica al momento de realizar la transferencia.',
];

// Componente para mostrar información de transferencia
const TransferInfo = () => (
  <div style={styles.infoBox}>
    <div style={styles.infoText}>
      <b>Beneficiario:</b>
      {' '}
      {TRANSFER_DATA.beneficiary}
    </div>
    <div style={styles.infoText}>
      <b>Banco destino:</b>
      {' '}
      {TRANSFER_DATA.bank}
    </div>
    <div style={styles.infoText}>
      <b>CLABE:</b>
      {' '}
      {TRANSFER_DATA.clabe}
    </div>
  </div>
);

// Componente para mostrar instrucciones
const InstructionsList = () => (
  <ol style={styles.instructionsList}>
    {INSTRUCTIONS.map((instruction, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <li key={`instruction-${index}`} style={styles.instructionItem}>
        {instruction}
      </li>
    ))}
  </ol>
);

// Componente para mostrar información del beneficiario
const BeneficiaryInfo = () => (
  <>
    <div style={styles.rightColumnTitle}>
      Información para alta del beneficiario
    </div>
    <div style={styles.beneficiaryInfo}>
      <b>Nombre</b>
      <br />
      {TRANSFER_DATA.beneficiary}
    </div>
    <div style={styles.beneficiaryInfo}>
      <b>RFC</b>
      <br />
      {TRANSFER_DATA.rfc}
    </div>
    <div style={styles.beneficiaryInfo}>
      <b>Teléfono</b>
      <br />
      {TRANSFER_DATA.phone}
    </div>
    <div style={styles.beneficiaryInfo}>
      <b>Domicilio</b>
      <br />
      {TRANSFER_DATA.address}
    </div>
  </>
);

// Componente para el botón de generar orden
const GenerateOrderButton = ({ onGenerate, loading }) => (
  <Button
    $accent
    style={styles.generateButton}
    onClick={onGenerate}
    loading={loading}
    disabled={loading}
  >
    <BarcodeOutlined style={styles.buttonIcon} />
    <Text>
      Generar orden de transferencia interbancaria
    </Text>
  </Button>
);

GenerateOrderButton.propTypes = {
  onGenerate: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

const ModalTransferencias = ({
  visible,
  onCancel,
  cbReferenciaBanco,
}) => {
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [previewBase64, setPreviewBase64] = useState();
  const [loading, setLoading] = useState(false);

  const showOrder = async () => {
    try {
      setLoading(true);

      const request = await cbReferenciaBanco();
      if (request) {
        const values = {
          folio: request.folio_banco,
          entidad: 1,
          base64: request.data,
        };
        const order = await printReferenceOrder(values);
        if (order) {
          setPreviewBase64(values.base64 || order?.data);
          setVisiblePreview(true);
        }
      }
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateOrder = async () => {
    await showOrder();
    onCancel();
  };

  return (
    <>
      <Modal
        visible={visible}
        footer={null}
        onCancel={onCancel}
        closable={!loading}
        centered
        maskClosable={false}
        {...styles.modal}
      >
        <Spin spinning={loading} tip="Cargando...">
          <div style={styles.container}>
            <div style={styles.header}>
              <Title level={4} $accent $bold color="#fff">
                Instrucciones para Transferencia Interbancaria
              </Title>
            </div>

            <Row gutter={0} style={{ margin: 0 }}>
              <Col xs={24} md={14} style={styles.leftColumn}>
                <Text>
                  Realiza tu transferencia interbancaria con los siguientes datos.
                </Text>
                <TransferInfo />
                <InstructionsList />
              </Col>

              <Col xs={24} md={10} style={styles.rightColumn}>
                <BeneficiaryInfo />
              </Col>
            </Row>

            <div style={styles.footer}>
              <GenerateOrderButton
                onGenerate={handleGenerateOrder}
                loading={loading}
              />
            </div>
          </div>
        </Spin>
      </Modal>

      <Previewer
        visible={visiblePreview}
        base64={previewBase64}
        isPDF
        onCancel={() => setVisiblePreview(false)}
      />
    </>
  );
};

export default ModalTransferencias;

ModalTransferencias.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  cbReferenciaBanco: PropTypes.func.isRequired,
};

ModalTransferencias.defaultProps = {
  onCancel: null,
};
